import { useEffect, useRef, useState, useCallback } from 'react'
import { callsApi } from '../../api/calls'
import { callsWs } from '../../ws/callsClient'
import { Avatar } from '../ui/Avatar'
import type { User } from '../../types'

interface Props {
  peer: User
  callType: 'voice' | 'video'
  /** 'caller' = we initiate; 'callee' = we received invite and accepted */
  mode?: 'caller' | 'callee'
  /** For callee mode: pre-set room ID from the invite */
  roomId?: string
  onClose: () => void
}

type CallState = 'connecting' | 'ringing' | 'active' | 'ended'

interface Signal {
  type: string
  room_id?: string
  to?: string
  from?: string
  payload?: unknown
}

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
]

export function CallModal({ peer, callType, mode = 'caller', roomId: incomingRoomId, onClose }: Props) {
  const [state, setState] = useState<CallState>('connecting')
  const [muted, setMuted] = useState(false)
  const [videoOff, setVideoOff] = useState(callType === 'voice')
  const [duration, setDuration] = useState(0)
  const [screenSharing, setScreenSharing] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [pipActive, setPipActive] = useState(false)
  const [recording, setRecording] = useState(false)
  const [togglingRecord, setTogglingRecord] = useState(false)

  const pcRef = useRef<RTCPeerConnection | null>(null)
  const localStream = useRef<MediaStream | null>(null)
  const roomIdRef = useRef<string>(incomingRoomId ?? '')
  const localVideoRef = useRef<HTMLVideoElement>(null)
  const remoteVideoRef = useRef<HTMLVideoElement>(null)
  // Удалённый поток держим отдельно и выводим звук через всегда-присутствующий
  // <audio> — иначе в аудиозвонке (без <video>) звук некуда выводить.
  const remoteStreamRef = useRef<MediaStream | null>(null)
  const remoteAudioRef = useRef<HTMLAudioElement>(null)
  const durationTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const screenTrackRef = useRef<MediaStreamTrack | null>(null)
  const offRef = useRef<(() => void) | null>(null)

  // При сворачивании/разворачивании <audio> перемонтируется — заново привязываем поток.
  useEffect(() => {
    if (remoteAudioRef.current && remoteStreamRef.current) {
      remoteAudioRef.current.srcObject = remoteStreamRef.current
      remoteAudioRef.current.play?.().catch(() => {})
    }
  }, [minimized])

  const peerName = [peer.first_name, peer.last_name].filter(Boolean).join(' ') || peer.username

  const send = useCallback((sig: Signal) => {
    callsWs.send(sig)
  }, [])

  const hangup = useCallback(() => {
    setState('ended')
    if (durationTimer.current) clearInterval(durationTimer.current)
    if (roomIdRef.current) {
      send({ type: 'call.end', room_id: roomIdRef.current })
      callsApi.end(roomIdRef.current).catch(() => {})
    }
    localStream.current?.getTracks().forEach((t) => t.stop())
    pcRef.current?.close()
    offRef.current?.()
    setTimeout(onClose, 1000)
  }, [send, onClose])

  const startDuration = () => {
    durationTimer.current = setInterval(() => setDuration((d) => d + 1), 1000)
  }

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    let pc: RTCPeerConnection

    const setup = async () => {
      // 1. Get local media
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: callType === 'video',
        })
        localStream.current = stream
        if (localVideoRef.current) localVideoRef.current.srcObject = stream
      } catch {
        try {
          localStream.current = await navigator.mediaDevices.getUserMedia({ audio: true })
        } catch {
          setState('ended')
          setTimeout(onClose, 1500)
          return
        }
      }

      // 2. Get TURN credentials
      let iceServers: RTCIceServer[] = ICE_SERVERS
      try {
        const turn = await callsApi.getTurnCredentials()
        if (turn.urls?.length) {
          iceServers = [
            ...ICE_SERVERS,
            ...turn.urls.map((url) => ({
              urls: url,
              username: turn.username,
              credential: turn.credential,
            })),
          ]
        }
      } catch { /* use STUN only */ }

      // 3. Create RTCPeerConnection
      pc = new RTCPeerConnection({ iceServers })
      pcRef.current = pc

      localStream.current.getTracks().forEach((t) => pc.addTrack(t, localStream.current!))

      pc.ontrack = (e) => {
        const stream = e.streams[0]
        if (!stream) return
        remoteStreamRef.current = stream
        // Видео (если элемент есть) и всегда — аудио для воспроизведения звука.
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = stream
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = stream
          remoteAudioRef.current.play?.().catch(() => {})
        }
      }

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') {
          setState('active')
          startDuration()
        } else if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
          hangup()
        }
      }

      pc.onicecandidate = (e) => {
        if (e.candidate && roomIdRef.current) {
          send({ type: 'ice.candidate', room_id: roomIdRef.current, to: peer.id, payload: e.candidate })
        }
      }

      // 4. Subscribe to calls WS signals
      offRef.current = callsWs.on(async (sig) => {
        switch (sig.type) {
          case 'call.accept': {
            // Callee accepted — now create and send SDP offer
            if (mode === 'caller') {
              const offer = await pc.createOffer()
              await pc.setLocalDescription(offer)
              send({ type: 'sdp.offer', room_id: roomIdRef.current, to: peer.id, payload: offer })
              setState('connecting')
            }
            break
          }
          case 'sdp.offer': {
            // We're the callee — respond with answer
            if (mode === 'callee') {
              const offer = sig.payload as RTCSessionDescriptionInit
              await pc.setRemoteDescription(new RTCSessionDescription(offer))
              const answer = await pc.createAnswer()
              await pc.setLocalDescription(answer)
              send({ type: 'sdp.answer', room_id: roomIdRef.current, to: sig.from ?? peer.id, payload: answer })
            }
            break
          }
          case 'sdp.answer': {
            if (mode === 'caller') {
              const answer = sig.payload as RTCSessionDescriptionInit
              await pc.setRemoteDescription(new RTCSessionDescription(answer))
            }
            break
          }
          case 'ice.candidate': {
            const candidate = sig.payload as RTCIceCandidateInit
            if (candidate) await pc.addIceCandidate(new RTCIceCandidate(candidate))
            break
          }
          case 'call.reject':
          case 'call.end':
          case 'call.busy':
            hangup()
            break
        }
      })

      // 5. Caller: invite → join room; Callee: join room + accept
      if (mode === 'caller') {
        try {
          const room = await callsApi.invite(peer.id, callType)
          roomIdRef.current = room.id
          setState('ringing')
          send({ type: 'room.join', room_id: room.id })
          // SDP offer sent only after receiving call.accept from callee
        } catch {
          hangup()
        }
      } else {
        // Callee mode: roomIdRef already set from incomingRoomId
        send({ type: 'room.join', room_id: roomIdRef.current })
        send({ type: 'call.accept', room_id: roomIdRef.current })
        setState('ringing')
        // SDP offer will arrive from caller, handled in 'sdp.offer' case above
      }
    }

    setup()

    return () => {
      if (durationTimer.current) clearInterval(durationTimer.current)
      localStream.current?.getTracks().forEach((t) => t.stop())
      pc?.close()
      offRef.current?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleScreenShare = async () => {
    if (screenSharing) {
      screenTrackRef.current?.stop()
      screenTrackRef.current = null
      const camTrack = localStream.current?.getVideoTracks()[0]
      if (camTrack && pcRef.current) {
        const sender = pcRef.current.getSenders().find((s) => s.track?.kind === 'video')
        if (sender) await sender.replaceTrack(camTrack)
      }
      if (localVideoRef.current && localStream.current) {
        localVideoRef.current.srcObject = localStream.current
      }
      setScreenSharing(false)
      if (roomIdRef.current) send({ type: 'peer.screen.off', room_id: roomIdRef.current })
    } else {
      try {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
        const track = screen.getVideoTracks()[0]
        screenTrackRef.current = track
        if (pcRef.current) {
          const sender = pcRef.current.getSenders().find((s) => s.track?.kind === 'video')
          if (sender) await sender.replaceTrack(track)
        }
        if (localVideoRef.current) localVideoRef.current.srcObject = screen
        track.onended = () => {
          setScreenSharing(false)
          screenTrackRef.current = null
          const camTrack = localStream.current?.getVideoTracks()[0]
          if (camTrack && pcRef.current) {
            const sender = pcRef.current.getSenders().find((s) => s.track?.kind === 'video')
            if (sender) sender.replaceTrack(camTrack).catch(() => {})
          }
          if (localVideoRef.current && localStream.current) {
            localVideoRef.current.srcObject = localStream.current
          }
        }
        setScreenSharing(true)
        if (roomIdRef.current) send({ type: 'peer.screen.on', room_id: roomIdRef.current })
      } catch { /* user cancelled */ }
    }
  }

  const toggleMute = () => {
    const audio = localStream.current?.getAudioTracks()[0]
    if (audio) {
      audio.enabled = muted
      setMuted(!muted)
      if (roomIdRef.current) {
        send({ type: muted ? 'peer.unmute' : 'peer.mute', room_id: roomIdRef.current })
      }
    }
  }

  const togglePiP = async () => {
    const video = remoteVideoRef.current
    if (!video) return
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture()
        setPipActive(false)
      } else {
        await video.requestPictureInPicture()
        setPipActive(true)
        video.addEventListener('leavepictureinpicture', () => setPipActive(false), { once: true })
      }
    } catch { /* PiP not supported */ }
  }

  const toggleVideo = () => {
    const video = localStream.current?.getVideoTracks()[0]
    if (video) {
      video.enabled = videoOff
      setVideoOff(!videoOff)
      if (roomIdRef.current) {
        send({ type: videoOff ? 'peer.video.on' : 'peer.video.off', room_id: roomIdRef.current })
      }
    }
  }

  const toggleRecording = async () => {
    if (!roomIdRef.current || togglingRecord) return
    setTogglingRecord(true)
    try {
      if (recording) {
        await callsApi.stopRecording(roomIdRef.current)
        setRecording(false)
      } else {
        await callsApi.startRecording(roomIdRef.current)
        setRecording(true)
      }
    } catch { /* ignore */ }
    finally { setTogglingRecord(false) }
  }

  if (minimized) {
    return (
      <div
        className="fixed bottom-4 right-4 z-50 w-48 bg-bg-primary rounded-2xl shadow-2xl overflow-hidden cursor-pointer animate-fade-in"
        onClick={() => setMinimized(false)}
      >
        {callType === 'video' && (
          <video ref={remoteVideoRef} autoPlay playsInline className="w-full aspect-video bg-black object-cover" />
        )}
        <audio ref={remoteAudioRef} autoPlay className="hidden" />
        <div className="px-3 py-2 flex items-center justify-between">
          <div>
            <p className="text-txt-primary text-xs font-medium truncate">{peerName}</p>
            <p className="text-accent text-[10px]">
              {state === 'active' ? formatDuration(duration) : 'Вызов...'}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); hangup() }}
            className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center text-white"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
            </svg>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="w-full max-w-md bg-bg-primary rounded-2xl overflow-hidden shadow-2xl">
        <audio ref={remoteAudioRef} autoPlay className="hidden" />

        {/* Video area — self-contained relative block */}
        {callType === 'video' ? (
          <div className="relative">
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full aspect-video bg-black object-cover"
            />
            {/* Local preview */}
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="absolute top-3 left-3 w-24 aspect-video rounded-xl bg-black object-cover border-2 border-white/20 z-10"
            />
            {/* Minimize + PiP */}
            <div className="absolute top-3 right-3 flex gap-2 z-10">
              {document.pictureInPictureEnabled && (
                <button
                  onClick={togglePiP}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors text-white
                    ${pipActive ? 'bg-accent' : 'bg-black/40 hover:bg-black/60'}`}
                  title="Картинка в картинке"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2"/>
                    <rect x="14" y="9" width="8" height="7" rx="1"/>
                  </svg>
                </button>
              )}
              <button
                onClick={() => setMinimized(true)}
                className="w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
                title="Свернуть"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
                  <line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
              </button>
            </div>
            {/* Name + status over video gradient */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-5 py-4 pointer-events-none">
              <p className="text-white font-semibold text-lg text-center">{peerName}</p>
              <p className="text-white/70 text-sm text-center mt-0.5">
                {state === 'connecting' && 'Соединение...'}
                {state === 'ringing' && (mode === 'caller' ? 'Вызов...' : 'Соединение...')}
                {state === 'active' && formatDuration(duration)}
                {state === 'ended' && 'Вызов завершён'}
              </p>
            </div>
          </div>
        ) : (
          /* Voice call: avatar + name in normal flow */
          <div className="flex flex-col items-center py-12 bg-gradient-to-b from-bg-primary to-bg-secondary">
            <Avatar name={peerName} src={peer.avatar_url} size={96} />
            <p className="text-txt-primary font-semibold text-lg mt-4">{peerName}</p>
            <p className="text-txt-secondary text-sm mt-1">
              {state === 'connecting' && 'Соединение...'}
              {state === 'ringing' && (mode === 'caller' ? 'Вызов...' : 'Соединение...')}
              {state === 'active' && formatDuration(duration)}
              {state === 'ended' && 'Вызов завершён'}
            </p>
            {/* Minimize for voice */}
            <button
              onClick={() => setMinimized(true)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-bg-hover flex items-center justify-center text-txt-secondary transition-colors hover:bg-bg-active"
              title="Свернуть"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
                <line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            </button>
          </div>
        )}

        <div className="flex items-center justify-center gap-4 px-5 py-5 bg-bg-primary">
          <button
            onClick={toggleMute}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors
              ${muted ? 'bg-red-500 text-white' : 'bg-bg-input text-txt-primary hover:bg-bg-hover'}`}
            title={muted ? 'Включить микрофон' : 'Выключить микрофон'}
          >
            {muted ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="1" y1="1" x2="23" y2="23"/>
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            )}
          </button>

          <button
            onClick={hangup}
            className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors"
            title="Завершить вызов"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
              <line x1="2" y1="22" x2="22" y2="2" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>

          {callType === 'video' && (
            <button
              onClick={toggleVideo}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors
                ${videoOff ? 'bg-red-500 text-white' : 'bg-bg-input text-txt-primary hover:bg-bg-hover'}`}
              title={videoOff ? 'Включить камеру' : 'Выключить камеру'}
            >
              {videoOff ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23"/>
                  <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
              )}
            </button>
          )}

          {callType === 'video' && (
            <button
              onClick={toggleScreenShare}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors
                ${screenSharing ? 'bg-accent text-white' : 'bg-bg-input text-txt-primary hover:bg-bg-hover'}`}
              title={screenSharing ? 'Остановить трансляцию' : 'Поделиться экраном'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
            </button>
          )}

          {state === 'active' && (
            <button
              onClick={toggleRecording}
              disabled={togglingRecord}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-50
                ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-bg-input text-txt-primary hover:bg-bg-hover'}`}
              title={recording ? 'Остановить запись' : 'Начать запись'}
            >
              <span className="text-base">{recording ? '⏹' : '⏺'}</span>
            </button>
          )}
          {callType === 'voice' && <div className="w-12 h-12" />}
        </div>
      </div>
    </div>
  )
}
